import {useEffect, useState} from 'react'
import {Form, Card, Badge, Alert} from 'react-bootstrap'
import axios from 'axios'
import {MessageDescriptor, useIntl} from 'react-intl'
import {URL} from '../../../gql/client'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {useGetUser} from '../../../store/userStore'
import {CHANNEL_CONFIG_MESSAGES as M} from '../channelConfigMessages'

// Decoración del encabezado, no texto traducible.
const SECTION_ICON = '\u2728'

type TriState = boolean | null

interface AiAssistantDomainConfig {
  enabled: boolean
  label: string
}

interface AiAssistantSectionProps {
  aiAssistantEnabled: TriState
  onAiAssistantEnabledChange: (value: TriState) => void
  aiAssistantDomains: Record<string, TriState>
  onAiAssistantDomainsChange: (value: Record<string, TriState>) => void
}

// El `value` es la clave de estado que viaja a la API; la etiqueta se traduce.
const TRI_STATE_OPTIONS: {value: string; label: MessageDescriptor}[] = [
  {value: 'inherit', label: M.triStateInherit},
  {value: 'on', label: M.triStateEnabled},
  {value: 'off', label: M.triStateDisabled},
]

const triStateToOption = (value: TriState): string =>
  value === true ? 'on' : value === false ? 'off' : 'inherit'

const optionToTriState = (option: string): TriState =>
  option === 'on' ? true : option === 'off' ? false : null

const TriStateSelect: React.FC<{
  id: string
  value: TriState
  onChange: (value: TriState) => void
  size?: 'sm'
}> = ({id, value, onChange, size}) => {
  const intl = useIntl()
  return (
  <Form.Select
    id={id}
    size={size}
    value={triStateToOption(value)}
    onChange={(e) => onChange(optionToTriState(e.target.value))}
    style={{maxWidth: 160}}
  >
    {TRI_STATE_OPTIONS.map((option) => (
      <option key={option.value} value={option.value}>
        {intl.formatMessage(option.label)}
      </option>
    ))}
  </Form.Select>
  )
}

export const AiAssistantSection = ({
  aiAssistantEnabled,
  onAiAssistantEnabledChange,
  aiAssistantDomains,
  onAiAssistantDomainsChange,
}: AiAssistantSectionProps) => {
  const user = useGetUser((state) => state.user)
  const [domains, setDomains] = useState<Record<string, AiAssistantDomainConfig>>({})
  const [loadingDomains, setLoadingDomains] = useState(false)

  useEffect(() => {
    const token = user?.token || window.localStorage.getItem(STORAGE_NAMES.TOKEN) || ''
    if (!token) return
    let cancelled = false
    setLoadingDomains(true)
    axios
      .get(`${URL}/api/ai-assistant/config`, {
        headers: {Authorization: `Bearer ${token}`},
      })
      .then((response) => {
        if (!cancelled) setDomains(response.data?.domains || {})
      })
      .catch(() => {
        if (!cancelled) setDomains({})
      })
      .finally(() => {
        if (!cancelled) setLoadingDomains(false)
      })
    return () => {
      cancelled = true
    }
  }, [user?.token])

  const handleDomainChange = (domainKey: string, value: TriState) => {
    onAiAssistantDomainsChange({...aiAssistantDomains, [domainKey]: value})
  }

  // AI Assistant is currently only configurable at the organization level (no
  // group/user override screen exists yet), so a plain on/off switch is
  // clearer than a tri-state "Inherit" selector — there's nothing above the
  // organization for it to meaningfully inherit from today.
  const intl = useIntl()
  const isEnabled = aiAssistantEnabled === true

  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Header className="bg-light border-0 py-3">
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center gap-2">
            <span className="fs-3">{SECTION_ICON}</span>
            <h5 className="fw-bold text-gray-800 mb-0">{intl.formatMessage(M.assistantTitle)}</h5>
          </div>
          <Badge bg={isEnabled ? 'success' : 'secondary'}>
            {intl.formatMessage(isEnabled ? M.enabled : M.disabled)}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            id="ai-assistant-enabled-switch"
            label={intl.formatMessage(M.assistantEnableLabel)}
            checked={isEnabled}
            onChange={(e) => onAiAssistantEnabledChange(e.target.checked)}
          />
          <Form.Text className="text-muted d-block mt-2">
            {intl.formatMessage(M.assistantEnableHint)}
          </Form.Text>
        </Form.Group>

        <hr />

        <Form.Label className="mb-2">{intl.formatMessage(M.assistantDomains)}</Form.Label>
        <Form.Text className="text-muted d-block mb-2">
          {intl.formatMessage(M.assistantDomainsHint)}
        </Form.Text>

        {loadingDomains && (
          <div className="text-muted fs-8">{intl.formatMessage(M.assistantLoadingDomains)}</div>
        )}

        {!loadingDomains && Object.keys(domains).length === 0 && (
          <Alert variant="info" className="mb-0">
            <small>{intl.formatMessage(M.assistantNoDomains)}</small>
          </Alert>
        )}

        {!loadingDomains &&
          Object.entries(domains).map(([domainKey, domainConfig]) => (
            <div
              key={domainKey}
              className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom"
            >
              <span className="fw-semibold text-gray-700">{domainConfig.label || domainKey}</span>
              <TriStateSelect
                id={`ai-assistant-domain-${domainKey}`}
                value={aiAssistantDomains[domainKey] ?? null}
                onChange={(value) => handleDomainChange(domainKey, value)}
                size="sm"
              />
            </div>
          ))}
      </Card.Body>
    </Card>
  )
}
